import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiBookOpen, FiClock, FiAward, FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';
import HeroSection from '../components/HeroSection';
import dataService from '../services/dataService';

const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const data = await dataService.getCourses();
        setCourses(data);
      } catch (error) {
        console.error('Failed to load courses:', error);
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, []);

  if (loading) {
    return (
      <div className="bg-slate-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50">
      <SEO
        pageType="courses"
        title="Chiropractic & Healing Courses | Professional Training | JIVA NADI Academy"
        description="Enroll in professional chiropractic and healing courses. Learn from Dr. Saurabh Singh and become a certified expert in non-surgical spinal care and holistic therapies."
        keywords="chiropractic courses, healing training, professional certification, spinal care education, holistic therapy courses, medical training"
      />

      {/* Reusable Hero Section */}
      <HeroSection
        title="Build Your Medical Legacy"
        subtitle="The Academy of Healing"
        description="Join India's premier institution for non-surgical spinal care. Learn directly from Dr. Saurabh Singh and become a certified expert in holistic science."
        image="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop"
        badge="The Academy of Healing"
        primaryButton={{
          text: "Enroll Now",
          href: "/contact"
        }}
        secondaryButton={{
          text: "View Courses",
          href: "#courses"
        }}
        trustIndicators={[
          { value: "UGC", label: "Recognized" },
          { value: "100%", label: "Practical" },
          { value: "Expert", label: "Training" }
        ]}
      />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          {courses.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No courses available at the moment.</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-10">
              {courses.map((course, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -15 }}
                  className="bg-white rounded-[3rem] overflow-hidden shadow-2xl shadow-emerald-900/5 group"
                >
                  <div className="h-64 relative overflow-hidden">
                    <img src={course.image} alt={course.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute top-6 left-6 bg-white/90 backdrop-blur px-4 py-2 rounded-2xl text-emerald-900 font-black text-lg">
                      {course.price}
                    </div>
                  </div>
                  <div className="p-10 space-y-6">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 px-4 py-1.5 rounded-full">{course.level}</span>
                      <div className="flex items-center text-gray-400 text-xs font-bold">
                        <SafeIcon icon={FiClock} className="mr-1.5" /> {course.duration}
                      </div>
                    </div>
                    <h3 className="text-3xl font-black text-gray-900 leading-tight">{course.title}</h3>
                    <p className="text-gray-500 leading-relaxed">{course.desc}</p>
                    <button className="w-full bg-emerald-600 hover:bg-emerald-900 text-white py-5 rounded-[2rem] font-bold transition-all flex items-center justify-center space-x-3 shadow-xl shadow-emerald-100">
                      <span>Apply Now</span>
                      <SafeIcon icon={FiArrowRight} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CoursesPage;